/**
 * Shared parts of the two audience pages of the Sept 2026 redesign: For Businesses
 * (ForBusinessesSlot.jsx) and For Venues (ForVenuesSlot.jsx). They port the package's
 * interior-page pieces (site/pages.css + site/flair.css: `.benefit-strip`, `.section-top`,
 * `.numbered-grid`, `.split-section`, `.paper-panel`, `.metric-list`, `.page-faq`) and the
 * two hero visuals (since U4 photos: BIZ-HERO-01 with the printed ad card, VEN-HERO-01 with
 * the ticket), the report photo, the category mosaic and image-topped cards onto the
 * components/site vocabulary and the brand tokens.
 *
 * Copy comes from the pages through props (these parts hold no product claims), except the
 * two hero visuals, whose words are part of the illustration: the printed coaster says it is
 * "an example of your ad space" and carries no figures.
 */
import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import {
  Eyebrow, FaqList, Heading, IconBadge, MarginNote, SiteCard, SiteImage, SiteSection, TextLink,
} from '@/components/site';
import { DemoOfferDialog } from '@/components/site/demo';
import { SITE_HEADER_OFFSET } from '../../layouts/PageStates';

/** The page's own <main> (PublicLayout's skip link targets it), clearing the fixed header. */
export function AudienceMain({ className, children, ...rest }) {
  return (
    <main id="main" className={cn(SITE_HEADER_OFFSET, 'site-light text-navy', className)} {...rest}>
      {children}
    </main>
  );
}

// The package's second breakpoint is 1050px (pages.css `max-width:1050px`): `min-[1051px]:`.
// Solid tints of the package's colour breaks, mixed from the brand tokens (color-mix, so the
// paper grid does not show through, as in the reference):
// - BAND_TINT: the even interior sections (flair.css), rule 27% into paper
// - FAQ_TINT: the FAQ band, lilac 60% into sheet
// - the numbered cards: lilac 45% / sage 55% into sheet
/** Tinted band behind the even interior sections. */
export const BAND_TINT = 'bg-[color-mix(in_srgb,theme(colors.brand.rule)_27%,theme(colors.brand.paper))]';
const FAQ_TINT = 'bg-[color-mix(in_srgb,theme(colors.brand.lilac)_60%,theme(colors.brand.sheet))]';

/** Interior pages' buttons: 15px text, 52px tall at every width (pages.css `.page-main .button`). */
export const PAGE_BUTTON = 'min-h-[3.25rem] text-[0.9375rem] nav:text-[0.9375rem]';
/** …and their text links at 15px at every width (`.page-main .text-link`). */
const PAGE_TEXT_LINK = 'text-[0.9375rem] nav:text-[0.9375rem]';

/**
 * The navy benefit strip under the hero: three short benefits, an orange top edge, inset
 * from the canvas edge; one column below the 800px nav breakpoint (icon in the left gutter).
 *
 * Props: `label` (the section's accessible name), `items` [{ icon, eyebrow, title, text }]
 * (`icon` a lucide component, decorative).
 */
export function BenefitStrip({ label, items = [] }) {
  return (
    <section
      aria-label={label}
      data-tone="navy"
      className="site-dark mx-[15px] border-t-4 border-brand-orange bg-navy text-brand-sheet shadow-[0_7px_0] shadow-navy/5 nav:mx-6 nav:border-t-[5px]"
    >
      <ul role="list" className="grid grid-cols-1 nav:grid-cols-3 nav:px-page-edge">
        {items.map(({ icon: Icon, eyebrow, title, text }, i) => (
          <li
            key={title}
            className={cn(
              // Phones: the icon sits in a left gutter (sized in rem, so it grows with the text).
              // The gutter needs at least 20em of screen (320px at the default text size); with
              // larger text the icon goes back above the text, as from 800px, so the words keep
              // their width. Below 800px only: nav: takes over from there.
              'relative min-w-0 border-b border-brand-sheet/20 px-[1.5625rem] py-[1.375rem] last:border-b-0',
              '[@media(min-width:20em)_and_(max-width:799.98px)]:pl-[5.1875rem]',
              'nav:border-b-0 nav:border-r nav:px-[22px] nav:py-7 nav:last:border-r-0 min-[1051px]:px-[38px] min-[1051px]:py-8',
              i === 1 && 'bg-brand-sheet/[0.03]',
              i === 2 && 'bg-brand-sheet/5',
            )}
          >
            <IconBadge
              tone="glass"
              className={cn(
                'mb-3 size-10 align-middle nav:mb-[15px] nav:size-[2.625rem]',
                '[@media(min-width:20em)_and_(max-width:799.98px)]:absolute [@media(min-width:20em)_and_(max-width:799.98px)]:left-6 [@media(min-width:20em)_and_(max-width:799.98px)]:top-[1.4375rem] [@media(min-width:20em)_and_(max-width:799.98px)]:mb-0',
              )}
            >
              {Icon ? <Icon strokeWidth={1.6} /> : null}
            </IconBadge>
            <Eyebrow tone="sheet" className="align-middle">{eyebrow}</Eyebrow>
            <p className="my-2 break-words font-display text-[1.75rem] font-medium leading-[1.2] nav:text-[1.8125rem]">{title}</p>
            <p className="m-0 break-words text-[0.9375rem] leading-[1.55] text-brand-sheet/85">{text}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * The head of an interior section: eyebrow and h2 on the left, a short muted intro pushed
 * to the right from 800px (stacked below it on phones).
 *
 * Props: `eyebrow`, `title` (h2 content, may hold <br />), `titleId`, `intro`.
 */
export function SectionTop({ eyebrow, title, titleId, intro }) {
  return (
    <div className="mb-10 min-w-0 nav:flex nav:items-end nav:justify-between nav:gap-10">
      <div className="min-w-0">
        <Eyebrow>{eyebrow}</Eyebrow>
        <Heading id={titleId} className="mb-0 mt-5">{title}</Heading>
      </div>
      {intro ? (
        <p className="mt-[22px] max-w-[24.375rem] break-words leading-[1.55] text-brand-muted nav:mt-0 nav:shrink-0">{intro}</p>
      ) : null}
    </div>
  );
}

// The three numbered cards: sheet, lilac and sage surfaces (flair.css), each number in a navy
// circle with a flat offset in the card's accent colour.
const CARD_TINTS = [
  { card: 'border-brand-rule bg-brand-sheet', badge: '' },
  { card: 'border-navy/10 bg-[color-mix(in_srgb,theme(colors.brand.lilac)_45%,theme(colors.brand.sheet))]', badge: 'shadow-brand-lilac' },
  { card: 'border-navy/10 bg-[color-mix(in_srgb,theme(colors.brand.sage)_55%,theme(colors.brand.sheet))]', badge: 'shadow-brand-sage' },
];

/**
 * Three numbered cards (the package's `.numbered-grid`), three columns from 800px.
 *
 * Props: `items` [{ title, text }] (h3 + muted paragraph); `ordered` = an <ol> (steps) instead
 * of a <ul>; `label` = the list's accessible name. The "01" numbers are decorative (the
 * list conveys the order).
 */
export function NumberedCards({ items = [], ordered = false, label }) {
  const List = ordered ? 'ol' : 'ul';
  return (
    <List role="list" aria-label={label} className="grid grid-cols-1 gap-7 nav:grid-cols-3 nav:gap-[38px]">
      {items.map(({ title, text }, i) => {
        const tint = CARD_TINTS[i % CARD_TINTS.length];
        return (
          <SiteCard
            as="li"
            key={title}
            padding="none"
            className={cn('min-w-0 rounded-[3px] px-[23px] pb-[39px] pt-[23px] shadow-site-card nav:px-[25px] nav:pb-9 nav:pt-[25px]', tint.card)}
          >
            <IconBadge size="sm" offset className={tint.badge}>
              {String(i + 1).padStart(2, '0')}
            </IconBadge>
            <Heading level={3} className="mb-4 mt-[15px] nav:mt-[26px]">{title}</Heading>
            <p className="break-words text-base leading-[1.55] text-brand-muted">{text}</p>
          </SiteCard>
        );
      })}
    </List>
  );
}

/**
 * The example-offer trigger drawn as the package's underlined text link ("Try an example
 * offer →", For Venues' guest panel) instead of DemoOfferButton's boxed button. Same
 * dialog (components/site/demo): it remembers the opener and returns focus to it on close.
 * Props: `children` (label), `className`.
 */
export function DemoOfferLink({ children, className }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <TextLink arrow aria-haspopup="dialog" onClick={() => setOpen(true)} className={cn(PAGE_TEXT_LINK, className)}>
        {children}
      </TextLink>
      <DemoOfferDialog open={open} onOpenChange={setOpen} />
    </>
  );
}

/**
 * The sheet panel with a rust top edge and a flat warm offset (`.paper-panel`).
 * Props: `as` (default 'div'), `className`, `children`, other props pass through.
 */
export function PaperPanel({ as = 'div', className, children, ...rest }) {
  return (
    <SiteCard
      as={as}
      accent="rust"
      padding="lg"
      className={cn('min-w-0 shadow-[7px_7px_0] shadow-brand-rule/60', className)}
      {...rest}
    >
      {children}
    </SiteCard>
  );
}

/**
 * A report's measures as a definition list (`.metric-list`): name on the left, how it is
 * recorded on the right, dashed rules between rows. Title it with the panel's eyebrow.
 * Props: `items` [{ name, detail }].
 */
export function MetricList({ items = [] }) {
  return (
    <dl className="mt-8">
      {items.map(({ name, detail }) => (
        <div key={name} className="flex flex-wrap justify-between gap-x-[30px] gap-y-1 border-t border-dashed border-brand-rule py-5 text-[0.9375rem]">
          <dt className="min-w-0 break-words font-medium">{name}</dt>
          <dd className="m-0 min-w-0 break-words text-right text-brand-muted">{detail}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * The FAQ band (`.page-faq`): pale lilac, eyebrow + h2 + "Ask us anything" on the left,
 * the questions on the right (0.8fr / 1.2fr from 800px). The section is a region named by
 * its h2; the list renders the FAQPage JSON-LD from exactly the items shown.
 *
 * Props: `id` (FaqList id prefix), `titleId`, `eyebrow`, `title`, `items` [{ q, a }].
 */
export function AudienceFaq({ id, titleId, eyebrow = 'Before you get started', title, items }) {
  return (
    <SiteSection
      divider
      aria-labelledby={titleId}
      className={FAQ_TINT}
      containerClassName="grid grid-cols-1 gap-[35px] nav:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] nav:gap-10 min-[1051px]:gap-20"
    >
      <div className="min-w-0">
        <Eyebrow>{eyebrow}</Eyebrow>
        <Heading id={titleId} className="mb-6 mt-5">{title}</Heading>
        <TextLink href="mailto:hello@tappd.app" arrow className={PAGE_TEXT_LINK}>Ask us anything</TextLink>
      </div>
      <FaqList
        id={id}
        items={items}
        jsonLd
        className="min-w-0"
        itemClassName="border-navy/20 data-[state=open]:bg-brand-sheet/55 data-[state=open]:px-4"
      />
    </SiteSection>
  );
}

/**
 * For Businesses hero visual (upgrade brief §3.3 item 1): BIZ-HERO-01 (a guest picks up a
 * coaster to read the ad on it) with the printed "what your ad could say" card, smaller,
 * overlapping the photo's lower-left corner. The card is a decorative mock-up, named once.
 * In the first viewport on desktop: the page's LCP image (priority).
 */
export function AdvertiserVisual() {
  return (
    <div className="relative mx-auto w-full max-w-[34.375rem] pb-[110px] pl-[18px] nav:mx-0 nav:max-w-none nav:pb-[96px] nav:pl-[56px]">
      <SiteImage
        id="BIZ-HERO-01"
        ratio="4x5"
        priority
        tone="navy"
        sizes="(min-width: 1808px) 660px, (min-width: 800px) 40vw, 92vw"
        className="border border-navy/15 shadow-[8px_9px_0_theme(colors.navy.DEFAULT/12%)]"
        testId="biz-hero-photo"
      />
      <MarginNote tone="rust" size="sm" tilt={9} className="absolute right-2 top-[-26px] z-[4] bg-brand-paper/90 px-2">
        what your ad
        <br />
        could say.
      </MarginNote>
      <div
        role="img"
        aria-label="An example of your ad space: a printed card that reads “Something good. Right here.”"
        className="absolute bottom-0 left-0 z-[2] w-[13rem] -rotate-[7deg] bg-brand-paper px-4 pt-4 text-navy shadow-[8px_9px_0] shadow-navy-stronger/50 nav:w-[14rem] min-[1051px]:w-[15.5rem]"
      >
        <div className="flex justify-between border-b border-navy pb-2.5 font-label text-[0.5625rem] uppercase">
          <span>Local favorites</span>
          <span>No. 001</span>
        </div>
        <p className="my-5 font-display text-[2.125rem] font-medium leading-[1.04] tracking-[-0.03em] min-[1051px]:text-[2.5rem]">
          Something
          <br />
          good.
          <br />
          <span className="font-hand text-[1.15em] text-brand-rust">Right here.</span>
        </p>
        <div className="-mx-4 border-t border-dashed border-navy" />
        <p className="py-3 text-center font-label text-[0.5625rem] uppercase">An example of your ad space</p>
      </div>
    </div>
  );
}

/**
 * HOME-REP-01: the monthly report on a laptop at a shop counter (upgrade brief §3.1 item 6,
 * §3.3 item 7), stamped "Sample, not campaign results": the screen shows demo data, never
 * results. Used by Home and For Businesses.
 */
export function ReportPhoto({ className, sizes = '(min-width: 1808px) 1483px, 100vw' }) {
  return (
    <figure className={cn('relative m-0', className)} data-testid="report-photo">
      <SiteImage
        id="HOME-REP-01"
        ratio="16x10"
        box="4x3"
        navBox="16x10"
        sizes={sizes}
        className="border border-brand-rule"
      />
      <figcaption className="absolute left-3 top-3 m-0 bg-navy px-3 py-2 font-label text-[11px] uppercase tracking-[0.06em] text-brand-sheet nav:left-5 nav:top-5">
        Sample, not campaign results
      </figcaption>
    </figure>
  );
}

/** One business per category (brief §3.3 item 5): the six photos and their captions. */
export const CATEGORY_MOSAIC = [
  { photo: 'BIZ-CAT-01', caption: 'Realtors' },
  { photo: 'BIZ-CAT-02', caption: 'Salons' },
  { photo: 'BIZ-CAT-03', caption: 'Gyms' },
  { photo: 'BIZ-CAT-04', caption: 'Shops' },
  { photo: 'BIZ-CAT-05', caption: 'Services' },
  { photo: 'BIZ-CAT-06', caption: 'You' },
];

/**
 * The six-tile photo mosaic, a caption per tile (3 across from 700px, 2 below). Items:
 * [{ photo, caption }].
 */
export function PhotoMosaic({ items = CATEGORY_MOSAIC, label }) {
  return (
    <ul role="list" aria-label={label} className="m-0 grid list-none grid-cols-1 gap-3 p-0 min-[360px]:grid-cols-2 min-[700px]:grid-cols-3 nav:gap-5">
      {items.map(({ photo, caption }) => (
        <li key={photo} className="min-w-0">
          <figure className="relative m-0">
            <SiteImage id={photo} ratio="1x1" box="4x3" sizes="(min-width: 1808px) 483px, (min-width: 700px) 32vw, 50vw" className="border border-brand-rule" />
            <figcaption className="absolute bottom-2.5 left-2.5 m-0 bg-brand-sheet px-2.5 py-1.5 font-label text-[11px] uppercase tracking-[0.06em] text-navy nav:bottom-3.5 nav:left-3.5 nav:text-xs">
              {caption}
            </figcaption>
          </figure>
        </li>
      ))}
    </ul>
  );
}

/**
 * Image-topped cards (For Venues benefits, brief §3.4 item 2): a square photo, then eyebrow,
 * title and one line. Items: [{ photo, eyebrow, title, text }].
 */
export function PhotoCards({ items = [], label, headingLevel = 3 }) {
  return (
    <ul role="list" aria-label={label} className="m-0 grid list-none grid-cols-1 gap-5 p-0 min-[700px]:grid-cols-3 nav:gap-6">
      {items.map(({ photo, eyebrow, title, text }) => (
        <li key={photo} className="flex min-w-0 flex-col border border-brand-rule bg-brand-sheet">
          <SiteImage id={photo} ratio="1x1" sizes="(min-width: 1808px) 483px, (min-width: 700px) 32vw, 100vw" />
          <div className="p-[22px] nav:p-6">
            {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
            <Heading level={headingLevel} size="minor" className="mb-2 mt-3 text-[1.625rem] leading-[1.12]">{title}</Heading>
            {text ? <p className="m-0 text-[0.9375rem] leading-[1.55] text-brand-muted">{text}</p> : null}
          </div>
        </li>
      ))}
    </ul>
  );
}

/**
 * For Venues hero visual (upgrade brief §3.4 item 1): VEN-HERO-01 (a bartender serves a pint
 * on a free coaster) with the tilted "The next round / On us." ticket overlapping it. In the
 * first viewport on desktop: the page's LCP image (priority).
 */
export function VenueHeroArt() {
  return (
    <div className="relative pb-[70px] nav:pb-[60px]">
      <SiteImage
        id="VEN-HERO-01"
        ratio="4x5"
        navRatio="4x5"
        priority
        tone="navy"
        sizes="(min-width: 1808px) 660px, (min-width: 800px) 40vw, 100vw"
        className="border border-navy/15"
        testId="venue-hero-photo"
      />
      <div className="absolute bottom-0 left-[22px] z-[2] w-[15.5rem] -rotate-[4deg] bg-brand-sheet p-[22px] text-navy shadow-[5px_5px_0] shadow-navy/35 nav:left-[-24px]">
        <Eyebrow>The next round</Eyebrow>
        <p className="my-[5px] font-display text-[3.5rem] font-medium leading-[1.1] tracking-[-0.025em]">On us.</p>
        <p className="text-[0.8125rem]">Sponsored by local businesses.</p>
      </div>
    </div>
  );
}
